import { PendingTransferStatusResponse, UserPendingTransferResponse } from '@/client/types.gen';

/**
 * Utility functions for pending transfer status checks
 */

// Transfer reason constants
export const TRANSFER_REASON_PROFESSIONAL_TO_OWNER = 'professional-to-owner';
export const TRANSFER_REASON_OWNER_TO_OWNER = 'owner-to-owner';

/**
 * Owner-to-owner transfers hand the property over instantly on claim; professional-to-owner
 * transfers build the records archive in the background first. Claim flows and cards branch
 * behaviour and copy on this distinction.
 */
export const isOwnerToOwnerTransfer = (transfer: { transfer_reason?: string | null }): boolean =>
	transfer.transfer_reason === TRANSFER_REASON_OWNER_TO_OWNER;

export const PendingTransferStatus = {
	Pending: 'pending',
	Processing: 'processing',
	Completed: 'completed',
	Cancelled: 'cancelled',
	Failed: 'failed',
} as const;

export type PendingTransferStatus = (typeof PendingTransferStatus)[keyof typeof PendingTransferStatus];

const ACTIONABLE_TRANSFER_STATUSES: readonly string[] = [
	PendingTransferStatus.Processing,
	PendingTransferStatus.Pending,
	PendingTransferStatus.Failed,
];

/** The minimal shape needed to decide whether a transfer is still claimable. */
type ClaimableTransferFields = Pick<UserPendingTransferResponse, 'status' | 'expires_at'>;
/** Any transfer shape carrying a status — the generated responses type it as enum or plain string. */
type TransferStatusFields = { status: string };

export const isExpiredTransfer = (transfer: ClaimableTransferFields): boolean => {
	return new Date(transfer.expires_at).getTime() < Date.now();
};

export const isProcessingTransfer = (transfer: TransferStatusFields): boolean => {
	return transfer.status === PendingTransferStatus.Processing;
};

export const isFailedTransfer = (transfer: TransferStatusFields): boolean => {
	return transfer.status === PendingTransferStatus.Failed;
};

/**
 * A transfer the recipient can still act on: it has an actionable status and its claim
 * window hasn't lapsed. Centralised so the claim route guard, the claim page, and the
 * properties-page incoming list all agree on what counts as claimable — an expired
 * transfer must never drive navigation or render as claimable.
 */
export const isActionableTransfer = (transfer: ClaimableTransferFields): boolean => {
	const hasActionableStatus = ACTIONABLE_TRANSFER_STATUSES.includes(transfer.status);
	return hasActionableStatus && !isExpiredTransfer(transfer);
};

/** Filters a (possibly undefined) transfer list down to those still claimable. */
export const selectActionableTransfers = (
	transfers: UserPendingTransferResponse[] | undefined,
): UserPendingTransferResponse[] => (transfers ?? []).filter(isActionableTransfer);

/**
 * Checks if a pending transfer has been cancelled
 */
export const isCancelled = (transfer: PendingTransferStatusResponse | null): boolean => {
	return transfer?.status === PendingTransferStatus.Cancelled;
};

/**
 * Checks if a pending transfer has been completed
 */
export const isComplete = (transfer: PendingTransferStatusResponse | null): boolean => {
	return transfer?.status === PendingTransferStatus.Completed;
};

/**
 * Checks if a pending transfer is still active (not cancelled or completed)
 */
export const isActive = (transfer: PendingTransferStatusResponse | null): boolean => {
	return transfer !== null && !isCancelled(transfer) && !isComplete(transfer);
};
